import { type ReactNode, type HTMLAttributes } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { twMerge } from 'tailwind-merge'
import { Card } from '@/components/composite/Card'
import { IconBadge } from '@/components/base/IconBadge'
import { itemDetailPopoverSubtitleVariants, arrowVariants } from './ItemDetailPopover.variants'

export interface ItemDetailPopoverProps extends HTMLAttributes<HTMLDivElement> {
  icon: ReactNode
  title: string
  subtitle?: string
  description: string
  accentColor?: 'none' | 'yellow' | 'green' | 'blue'
  footer?: ReactNode
  children: ReactNode // The trigger element
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const ItemDetailPopover = ({ 
  icon, 
  title, 
  subtitle, 
  description, 
  accentColor = 'none',
  footer,
  children,
  open,
  onOpenChange,
  className,
  ...props
}: ItemDetailPopoverProps) => {
  return (
    <Popover.Root onOpenChange={onOpenChange} open={open}>
      <Popover.Trigger asChild>
        {children}
      </Popover.Trigger>
      
      <Popover.Portal>
        <Popover.Content
          align="center"
          alignOffset={0}
          className="w-[280px] z-[9999] drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]"
          side="top"
        >
          <Card 
            accentColor={accentColor} 
            className={twMerge(
              'relative overflow-hidden [&>div]:p-0',
              'bg-primary-background',
              className
            )}
            {...props}
          >
            <div className="p-4 pb-3">
              <div className="flex items-center gap-3 mb-2">
                <IconBadge accentColor={accentColor}>
                  {icon}
                </IconBadge>
                
                <div className="flex flex-col">
                  <h3 className="text-white font-formula font-bold text-md">
                    {title}
                  </h3>
                  {subtitle ? (
                    <p className={itemDetailPopoverSubtitleVariants({ accentColor })}>
                      {subtitle}
                    </p>
                  ) : null}
                </div>
              </div>

              <p className="text-base text-white/65">
                {description}
              </p>
            </div>

            {footer ? (
              <div className="flex justify-center rounded-b-[10px] bg-[linear-gradient(180deg,rgba(255,255,255,0.25)_0%,rgba(153,153,153,0.30)_100%)]">
                {footer}
              </div>
            ) : null}
          </Card>
          
          <Popover.Arrow 
            className={arrowVariants({ hasFooter: Boolean(footer) })}
            height={6} 
            width={12} 
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

ItemDetailPopover.displayName = 'ItemDetailPopover'